import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

const SubscriptionContext = createContext(null);

const DEFAULT_PLATFORM_SETTINGS = {
  commissionPercent: 12,
  expertTrialDays: 180,
  expertMinRate: 3000,
  expertMaxRate: 100000,
  bookingCancellationHours: 24,
  currency: "RUB"
};

export function SubscriptionProvider({ children }) {
  const [platformSettings, setPlatformSettings] = useState(DEFAULT_PLATFORM_SETTINGS);
  const [expertSubscription, setExpertSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlatformSettings = useCallback(async (retries = 3) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const { data, error } = await supabase
        .from("platform_settings")
        .select("*")
        .limit(1)
        .maybeSingle();

      clearTimeout(timeoutId);

      if (!error && data) {
        setPlatformSettings({
          id: data.id,
          commissionPercent: Number(data.commission_percent),
          expertTrialDays: data.expert_trial_days,
          expertMinRate: data.expert_min_rate,
          expertMaxRate: data.expert_max_rate,
          bookingCancellationHours: data.booking_cancellation_hours,
          currency: data.currency
        });
        return true;
      }

      if (error) {
        const isNetworkError = 
          error.message === "Failed to fetch" || 
          error.message?.includes("NetworkError") || 
          error.message?.includes("network") ||
          error.code === "PGRST000" ||
          error.message?.includes("violates row-level security");

        if (isNetworkError && retries > 0) {
          await new Promise((r) => setTimeout(r, 2000));
          return fetchPlatformSettings(retries - 1);
        }
        
        console.info("Platform settings unavailable, using defaults");
        return false;
      }
    } catch (err) {
      const isNetworkError = 
        err.name === "AbortError" ||
        err.message === "Failed to fetch" ||
        err.message?.includes("network");

      if (isNetworkError && retries > 0) {
        await new Promise((r) => setTimeout(r, 2000));
        return fetchPlatformSettings(retries - 1);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlatformSettings();
  }, [fetchPlatformSettings]);

  const fetchExpertSubscription = useCallback(async (expertId) => {
    const { data, error } = await supabase
      .from("expert_subscriptions")
      .select("*")
      .eq("expert_id", expertId)
      .single();

    if (!error && data) {
      const now = new Date();
      const trialEnd = new Date(data.trial_end);
      const daysLeft = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
      const isTrialActive = data.is_active && trialEnd > now;

      const sub = {
        id: data.id,
        expertId: data.expert_id,
        plan: data.plan,
        trialStart: data.trial_start,
        trialEnd: data.trial_end,
        isActive: data.is_active,
        isTrialActive,
        daysLeft
      };
      setExpertSubscription(sub);
      return sub;
    }
    return null;
  }, []);

  const updatePlatformSettings = async (updates) => {
    if (!platformSettings.id) return;

    const dbUpdates = {};
    if (updates.commissionPercent !== undefined) dbUpdates.commission_percent = updates.commissionPercent;
    if (updates.expertTrialDays !== undefined) dbUpdates.expert_trial_days = updates.expertTrialDays;
    if (updates.expertMinRate !== undefined) dbUpdates.expert_min_rate = updates.expertMinRate;
    if (updates.expertMaxRate !== undefined) dbUpdates.expert_max_rate = updates.expertMaxRate;
    if (updates.bookingCancellationHours !== undefined) dbUpdates.booking_cancellation_hours = updates.bookingCancellationHours;
    dbUpdates.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from("platform_settings")
      .update(dbUpdates)
      .eq("id", platformSettings.id);

    if (!error) {
      setPlatformSettings((prev) => ({ ...prev, ...updates }));
      return true;
    }
    return false;
  };

  const calculateCommission = (expertRate) => {
    const commission = platformSettings.commissionPercent;
    const platformFee = Math.round(expertRate * (commission / 100));
    const expertEarnings = expertRate - platformFee;
    return {
      clientPays: expertRate,
      platformFee,
      expertEarnings,
      commissionPercent: commission
    };
  };

  return (
    <SubscriptionContext.Provider
      value={{
        platformSettings,
        expertSubscription,
        loading,
        fetchExpertSubscription,
        updatePlatformSettings,
        calculateCommission,
        refreshSettings: fetchPlatformSettings
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}
